import { useEffect, useRef } from "react";

/* Pure-code particle constellation: drifting nodes + proximity links.
   No images, 60fps, paused when offscreen / hidden tab. */
export default function HeroCanvas({ className = "" }) {
    const ref = useRef(null);

    useEffect(() => {
        const canvas = ref.current;
        if (!canvas) return;
        const ctx = canvas.getContext("2d");
        const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
        const dpr = Math.min(window.devicePixelRatio || 1, 1.5);
        let raf = null;
        let w = 0;
        let h = 0;
        const P = [];
        const N = 64;

        const init = () => {
            w = canvas.offsetWidth;
            h = canvas.offsetHeight;
            canvas.width = w * dpr;
            canvas.height = h * dpr;
            ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
            P.length = 0;
            for (let i = 0; i < N; i++) {
                P.push({
                    x: Math.random() * w,
                    y: Math.random() * h,
                    vx: (Math.random() - 0.5) * 0.22,
                    vy: (Math.random() - 0.5) * 0.22,
                    r: Math.random() * 1.4 + 0.4,
                });
            }
        };

        const frame = () => {
            ctx.clearRect(0, 0, w, h);
            for (const p of P) {
                p.x += p.vx;
                p.y += p.vy;
                if (p.x < 0 || p.x > w) p.vx *= -1;
                if (p.y < 0 || p.y > h) p.vy *= -1;
            }
            for (let i = 0; i < P.length; i++) {
                for (let j = i + 1; j < P.length; j++) {
                    const a = P[i];
                    const b = P[j];
                    const d = Math.hypot(a.x - b.x, a.y - b.y);
                    if (d < 130) {
                        ctx.strokeStyle = `rgba(0, 209, 255, ${(1 - d / 130) * 0.12})`;
                        ctx.lineWidth = 1;
                        ctx.beginPath();
                        ctx.moveTo(a.x, a.y);
                        ctx.lineTo(b.x, b.y);
                        ctx.stroke();
                    }
                }
            }
            for (const p of P) {
                ctx.fillStyle = "rgba(125, 195, 255, 0.65)";
                ctx.beginPath();
                ctx.arc(p.x, p.y, p.r, 0, 7);
                ctx.fill();
            }
            if (!reduced) raf = requestAnimationFrame(frame);
        };

        init();
        frame();
        const ro = new ResizeObserver(() => {
            init();
            if (reduced) frame();
        });
        ro.observe(canvas);

        let hidden = false;
        const onVis = () => {
            hidden = document.hidden;
            if (!hidden && !raf && !reduced) raf = requestAnimationFrame(frame);
            if (hidden && raf) {
                cancelAnimationFrame(raf);
                raf = null;
            }
        };
        document.addEventListener("visibilitychange", onVis);

        return () => {
            if (raf) cancelAnimationFrame(raf);
            ro.disconnect();
            document.removeEventListener("visibilitychange", onVis);
        };
    }, []);

    return <canvas ref={ref} className={`absolute inset-0 h-full w-full ${className}`} aria-hidden="true" />;
}
